import { useCallback } from 'react';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  PinErrorCode,
  type PinErrorDetail,
  type PinResponse,
  type PinSourceContext,
} from '@models/api/space-question-pin';
import { pinQuestionInSpace, unpinQuestionInSpace } from '@api-client/api-client';
import {
  questionsSpaceKey,
  invalidateQuestionDetail,
  questionEntityKey,
  updateQuestionEntity,
  getQuestionEntity,
  pinnedQuestionsSpaceKey,
} from '@lib/cache/questions';
import type { QuestionCacheItem, NormalizedPage } from '@lib/cache/questions';
import { useAuth } from '@providers';
import { useToast } from './use-toast';
import { useAmplitudeTracking } from './useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { logger } from '@utils/logger';

export interface PinMutationInput {
  spaceId: string;
  spaceIdentifier?: string;
  questionId: string;
  questionSlug?: string | null;
  /** Label shown in toast + analytics for context (e.g. the space name). */
  spaceName?: string;
  /**
   * Analytics surface label. Emitted as `source_context` on every pin/unpin
   * event — not `source` — to avoid clobbering the base `source` attribution
   * field that useAmplitudeTracking adds (platform: "web"/"native").
   */
  sourceContext?: PinSourceContext;
}

type SpaceQuestionsCache = InfiniteData<NormalizedPage<Record<string, unknown>>>;

type PinFields = Pick<
  NonNullable<QuestionCacheItem['detail']>,
  'pinned_at' | 'pinned_by_user_id' | 'pinned_by_name'
>;

type PinMode = 'pin' | 'unpin';

interface OptimisticSnapshot {
  spaceId: string;
  questionId: string;
  entitySnapshot: QuestionCacheItem | undefined;
  pinnedIdsSnapshot: string[] | undefined;
  pageSnapshots: [readonly unknown[], SpaceQuestionsCache | undefined][];
}

/**
 * Slot an unpinned ID back into chronological DESC order among its unpinned
 * peers, skipping any still-pinned IDs at the head of the list.
 *
 * Exported for unit testing — not intended as public API.
 *
 * @param ids           Remaining IDs (excluding targetId)
 * @param targetId      The ID being re-slotted
 * @param targetCreatedAt ISO timestamp of the target question
 * @param isPinned      Returns true when the given ID is still pinned
 * @param getCreatedAt  Returns the ISO created_at for the given ID
 */
export const insertUnpinnedIdByCreatedAt = (
  ids: string[],
  targetId: string,
  targetCreatedAt: string,
  isPinned: (id: string) => boolean,
  getCreatedAt: (id: string) => string,
): string[] => {
  const targetTime = new Date(targetCreatedAt).getTime();
  const idx = ids.findIndex(id => {
    if (isPinned(id)) return false;
    return new Date(getCreatedAt(id)).getTime() < targetTime;
  });
  if (idx === -1) return [...ids, targetId];
  return [...ids.slice(0, idx), targetId, ...ids.slice(idx)];
};

/**
 * Reorder page-0 IDs: pin prepends the question; unpin re-slots it by
 * `created_at` DESC among unpinned peers. Returns the original array
 * unchanged (by reference) when the question is absent, so TanStack Query's
 * `setQueryData` can bail on identity — no re-renders for unrelated caches.
 *
 * Exported for unit testing — not intended as public API.
 */
export const reorderPage0Ids = (
  ids: string[],
  questionId: string,
  mode: PinMode,
  getCreatedAt: (id: string) => string,
  isPinned: (id: string) => boolean,
): string[] => {
  if (!ids.includes(questionId)) return ids;
  const without = ids.filter(id => id !== questionId);
  if (mode === 'pin') return [questionId, ...without];
  const createdAt = getCreatedAt(questionId);
  return insertUnpinnedIdByCreatedAt(without, questionId, createdAt, isPinned, getCreatedAt);
};

/**
 * Patch pin fields onto the entity store for all variants of a space cache.
 * Pin metadata is space-scoped by design. We write to the entity store (not
 * paginated caches) because QuestionCard only shows the pin badge when
 * `communityContext?.spaceId` is set, so cross-feed staleness is not
 * user-visible. Don't widen the badge gating without revisiting this.
 */
const patchEntityPinFields = (
  queryClient: ReturnType<typeof useQueryClient>,
  questionId: string,
  patch: PinFields,
) => {
  updateQuestionEntity(queryClient, questionId, existing => ({
    ...existing,
    detail: existing.detail ? { ...existing.detail, ...patch } : existing.detail,
  }));
};

const updateCachesForSpace = (
  queryClient: ReturnType<typeof useQueryClient>,
  spaceId: string,
  questionId: string,
  mode: PinMode,
  patch: PinFields,
): OptimisticSnapshot => {
  const queryKey = questionsSpaceKey(spaceId);
  const entries = queryClient.getQueriesData<SpaceQuestionsCache>({ queryKey });

  // Snapshot current state for rollback.
  const entitySnapshot = getQuestionEntity(queryClient, questionId);
  const pinnedIdsSnapshot = queryClient.getQueryData<string[]>(pinnedQuestionsSpaceKey(spaceId));

  // Update entity store with optimistic pin fields.
  patchEntityPinFields(queryClient, questionId, patch);

  // Update space-scoped pinned IDs cache entry (subscribed to by QuestionCard directly).
  const currentPinnedIds = pinnedIdsSnapshot ?? [];
  const newPinnedIds =
    mode === 'pin'
      ? [...currentPinnedIds.filter(id => id !== questionId), questionId]
      : currentPinnedIds.filter(id => id !== questionId);
  queryClient.setQueryData(pinnedQuestionsSpaceKey(spaceId), newPinnedIds);

  // Reorder page-0 IDs in all cache variants (unfiltered + topic-filtered).
  // Post-entity-update lookups reflect the optimistic state, which is correct
  // for sibling isPinned checks (sibling entities are unchanged).
  // Epoch fallback can misplace a sibling not yet in the entity store (rare —
  // page-0 siblings are normally seeded by the same feed fetch that produced
  // this page). Self-heals via onSettled's invalidate below, so not worth
  // tracking real created_at for a sub-second, non-recurring mis-sort.
  // Upgrade path if it ever matters: skip reordering entirely when any
  // sibling's created_at is unknown, instead of guessing.
  const getCreatedAt = (id: string): string =>
    getQuestionEntity(queryClient, id)?.created_at ?? '2000-01-01T00:00:00Z';
  const isPinned = (id: string): boolean =>
    Boolean(getQuestionEntity(queryClient, id)?.detail?.pinned_at);

  entries.forEach(([key]) => {
    queryClient.setQueryData<SpaceQuestionsCache>(key, old => {
      if (!old?.pages?.length) return old;
      const [firstPage, ...restPages] = old.pages;
      if (!firstPage.ids.includes(questionId)) {
        // Entity is already updated; deeper pages get correct pin state on invalidation.
        return old;
      }
      const newIds = reorderPage0Ids(firstPage.ids, questionId, mode, getCreatedAt, isPinned);
      if (newIds === firstPage.ids) return old;
      return { ...old, pages: [{ ...firstPage, ids: newIds }, ...restPages] };
    });
  });

  return { spaceId, questionId, entitySnapshot, pinnedIdsSnapshot, pageSnapshots: entries };
};

// Full-entity restore is intentional: onSettled always invalidates and refetches,
// so any concurrent interaction write transiently lost here is recovered immediately.
// Surgical pin-field-only restore would have the same visible window at higher complexity.
const restoreSnapshot = (
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: OptimisticSnapshot | undefined,
) => {
  if (!snapshot) return;
  queryClient.setQueryData(questionEntityKey(snapshot.questionId), snapshot.entitySnapshot);
  queryClient.setQueryData(pinnedQuestionsSpaceKey(snapshot.spaceId), snapshot.pinnedIdsSnapshot);
  snapshot.pageSnapshots.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });
};

const invalidateSpaceQuestions = (
  queryClient: ReturnType<typeof useQueryClient>,
  spaceId: string,
  questionId: string,
  questionSlug?: string | null,
) => {
  void queryClient.invalidateQueries({ queryKey: questionsSpaceKey(spaceId) });
  // The post itself, on every route that shows who pinned it: by id, and by the slug the caller
  // named. With no slug to name, no slug-routed page is swept.
  void invalidateQuestionDetail(queryClient, { questionId, slug: questionSlug ?? null });
};

const getPinErrorDetail = (error: unknown): PinErrorDetail | null => {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data as { detail?: unknown } | undefined;
  const detail = data?.detail;
  if (detail && typeof detail === 'object' && 'code' in detail) {
    return detail as PinErrorDetail;
  }
  return null;
};

const getHttpStatus = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

interface PinErrorToast {
  title: string;
  description: string;
}

const toastForPinError = (error: unknown, spaceName?: string): PinErrorToast => {
  const detail = getPinErrorDetail(error);
  if (detail?.code === PinErrorCode.pin_limit_reached) {
    return {
      title: `You've reached the pin limit`,
      description: `${spaceName ?? 'This space'} already has ${detail.limit} pinned posts. Unpin one first.`,
    };
  }
  if (detail?.code === PinErrorCode.question_not_in_space) {
    return {
      title: 'Post not in this space',
      description: `This post isn't in ${spaceName ?? 'this space'} anymore.`,
    };
  }
  if (getHttpStatus(error) === 403) {
    return {
      title: 'Only admins can pin posts',
      description: `You need to be an admin in ${spaceName ?? 'this space'} to pin posts.`,
    };
  }
  return {
    title: `Couldn't pin post`,
    description: 'Something went wrong. Please try again.',
  };
};

const toastForUnpinError = (error: unknown, spaceName?: string): PinErrorToast => {
  const detail = getPinErrorDetail(error);
  if (detail?.code === PinErrorCode.question_not_in_space) {
    return {
      title: 'Post not in this space',
      description: `This post isn't in ${spaceName ?? 'this space'} anymore.`,
    };
  }
  if (getHttpStatus(error) === 403) {
    return {
      title: 'Only admins can unpin posts',
      description: `You need to be an admin in ${spaceName ?? 'this space'} to unpin posts.`,
    };
  }
  return {
    title: `Couldn't unpin post`,
    description: 'Something went wrong. Please try again.',
  };
};

export const usePinQuestion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { trackEvent } = useAmplitudeTracking();
  const { user } = useAuth();

  const pinMutation = useMutation<
    PinResponse,
    unknown,
    PinMutationInput,
    OptimisticSnapshot | undefined
  >({
    mutationFn: async ({ spaceId, spaceIdentifier, questionId }) => {
      const response = await pinQuestionInSpace(spaceIdentifier ?? spaceId, questionId);
      return response.data;
    },
    onMutate: async input => {
      await queryClient.cancelQueries({ queryKey: questionsSpaceKey(input.spaceId) });
      return updateCachesForSpace(queryClient, input.spaceId, input.questionId, 'pin', {
        pinned_at: new Date().toISOString(),
        pinned_by_user_id: user?.id ?? null,
        pinned_by_name: user?.full_name ?? null,
      });
    },
    onError: (error, input, snapshot) => {
      restoreSnapshot(queryClient, snapshot);
      const { title, description } = toastForPinError(error, input.spaceName);
      toast({ title, description, variant: 'error' });
      const detail = getPinErrorDetail(error);
      if (detail?.code === PinErrorCode.pin_limit_reached) {
        trackEvent(ANALYTICS_EVENTS.pin_limit_reached, {
          space_id: input.spaceId,
          question_id: input.questionId,
          source_context: input.sourceContext ?? 'unknown',
          limit: detail.limit,
          current: detail.current,
        });
      }
      trackEvent(ANALYTICS_EVENTS.post_pin_failed, {
        space_id: input.spaceId,
        question_id: input.questionId,
        source_context: input.sourceContext ?? 'unknown',
        error_code: detail?.code ?? null,
        http_status: getHttpStatus(error) ?? null,
      });
      logger.error('Pin question failed', error);
    },
    onSuccess: (data, input) => {
      // Overlay the server's authoritative pin metadata on top of the optimistic
      // local-user stamp. Matters for the idempotent re-pin race: if another admin
      // pinned first, backend returns that admin's id/name, not ours.
      //
      // Runtime guard: if the server returns an unexpected shape (missing pinned_at),
      // keep the optimistic stamp rather than clearing it. The settle-invalidate
      // still fires on the next tick, so any real divergence resolves then.
      if (data?.pinned_at) {
        patchEntityPinFields(queryClient, input.questionId, {
          pinned_at: data.pinned_at,
          pinned_by_user_id: data.pinned_by_user_id ?? null,
          pinned_by_name: data.pinned_by_name ?? null,
        });
      } else {
        logger.warn('Pin succeeded but response missing pinned_at; keeping optimistic stamp', {
          space_id: input.spaceId,
          question_id: input.questionId,
        });
      }
      trackEvent(ANALYTICS_EVENTS.post_pin_succeeded, {
        space_id: input.spaceId,
        question_id: input.questionId,
        source_context: input.sourceContext ?? 'unknown',
      });
    },
    onSettled: (_data, _error, input) => {
      invalidateSpaceQuestions(queryClient, input.spaceId, input.questionId, input.questionSlug);
    },
  });

  const unpinMutation = useMutation<
    undefined,
    unknown,
    PinMutationInput,
    OptimisticSnapshot | undefined
  >({
    mutationFn: async ({ spaceId, spaceIdentifier, questionId }) => {
      await unpinQuestionInSpace(spaceIdentifier ?? spaceId, questionId);
      return undefined;
    },
    onMutate: async input => {
      await queryClient.cancelQueries({ queryKey: questionsSpaceKey(input.spaceId) });
      return updateCachesForSpace(queryClient, input.spaceId, input.questionId, 'unpin', {
        pinned_at: null,
        pinned_by_user_id: null,
        pinned_by_name: null,
      });
    },
    onError: (error, input, snapshot) => {
      restoreSnapshot(queryClient, snapshot);
      const { title, description } = toastForUnpinError(error, input.spaceName);
      toast({ title, description, variant: 'error' });
      trackEvent(ANALYTICS_EVENTS.post_unpin_failed, {
        space_id: input.spaceId,
        question_id: input.questionId,
        source_context: input.sourceContext ?? 'unknown',
        error_code: getPinErrorDetail(error)?.code ?? null,
        http_status: getHttpStatus(error) ?? null,
      });
      logger.error('Unpin question failed', error);
    },
    onSuccess: (_data, input) => {
      trackEvent(ANALYTICS_EVENTS.post_unpin_succeeded, {
        space_id: input.spaceId,
        question_id: input.questionId,
        source_context: input.sourceContext ?? 'unknown',
      });
    },
    onSettled: (_data, _error, input) => {
      invalidateSpaceQuestions(queryClient, input.spaceId, input.questionId, input.questionSlug);
    },
  });

  const pin = useCallback(
    (input: PinMutationInput) => {
      trackEvent(ANALYTICS_EVENTS.post_pin_confirmed, {
        space_id: input.spaceId,
        question_id: input.questionId,
        source_context: input.sourceContext ?? 'unknown',
      });
      return pinMutation.mutateAsync(input);
    },
    [pinMutation, trackEvent],
  );

  const unpin = useCallback(
    (input: PinMutationInput) => {
      trackEvent(ANALYTICS_EVENTS.post_unpin_confirmed, {
        space_id: input.spaceId,
        question_id: input.questionId,
        source_context: input.sourceContext ?? 'unknown',
      });
      return unpinMutation.mutateAsync(input);
    },
    [unpinMutation, trackEvent],
  );

  return {
    pin,
    unpin,
    isPinning: pinMutation.isPending,
    isUnpinning: unpinMutation.isPending,
    isPending: pinMutation.isPending || unpinMutation.isPending,
  };
};
